import React from 'react';
import { Mail, MapPin, Phone, Linkedin, Facebook, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/yuzu-recruitment-services', label: 'LinkedIn' },
    { icon: Facebook, href: 'https://www.facebook.com/share/1CUYL3vLLk/?mibextid=wwXIfr', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/yuzu.recruitment?igsh=NjFwZnphOHVpeW80', label: 'Instagram' },
  ];

  const phoneNumbers = [
    { number: '+27 66 248 4734', tel: '+27662484734' },
    { number: '+27 76 899 9014', tel: '+27768999014' },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <h3 className="text-2xl font-bold text-white mb-2">
              Yu<span className="text-[#FFD000]">Zu</span>
            </h3>
            <p className="text-sm text-gray-400 mb-1">Recruitment Services</p>
            <p className="text-xs text-gray-500 italic mb-4">Zest for the right Talent</p>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Founder-led hospitality recruitment for operational stability.
            </p>
            {/* Social Media Icons */}
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-800 hover:bg-[#FFD000] p-2 rounded-full transition-all duration-200 group"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="h-4 w-4 text-gray-400 group-hover:text-black transition-colors duration-200" />
                </a>
              ))}
            </div>
            {/* Verification Badge */}
            <div className="mt-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_d9f78479-b1ec-4ee5-97ee-4214e41b9e45/artifacts/w0ivev8u_Verification%20badge.png"
                alt="Registered Company - Verified by Company Partners"
                className="h-16 w-auto rounded"
                data-testid="verification-badge"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('method')} className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
                  Our Method
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('vacancies')} className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
                  Current Vacancies
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('insights')} className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
                  Insights
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
                  About
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Our Services</h4>
            <ul className="space-y-2">
              <li className="text-gray-400 text-sm">Hospitality Recruitment</li>
              <li className="text-gray-400 text-sm">Operational Risk Audit</li>
              <li className="text-gray-400 text-sm">Leadership Placement</li>
              <li className="text-gray-400 text-sm">Workforce Consulting</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              {phoneNumbers.map((phone, index) => (
                <li key={index} className="flex items-start">
                  <Phone className="h-4 w-4 text-[#FFD000] mr-2 mt-1 flex-shrink-0" />
                  <a href={`tel:${phone.tel}`} className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
                    {phone.number}
                  </a>
                </li>
              ))}
              <li className="flex items-start">
                <Mail className="h-4 w-4 text-[#FFD000] mr-2 mt-1 flex-shrink-0" />
                <a href="mailto:info@yuzurecruit.co.za" className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
                  info@yuzurecruit.co.za
                </a>
              </li>
              <li className="flex items-start">
                <MapPin className="h-4 w-4 text-[#FFD000] mr-2 mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  Johannesburg, Gauteng
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            © 2026 YuZu Recruitment Services. All rights reserved.
          </p>
          <div className="flex space-x-6">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-gray-400 hover:text-[#FFD000] transition-colors duration-200 text-sm">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
